import React, { useRef, useState } from "react";
import Header from "../layout/Header";
import { checkValidData } from "../../utils/validate";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import { auth } from "../../utils/firebase";
import { useDispatch } from "react-redux";
import { addUser } from "../../store/slices/userSlice";
import { BG_URL, USER_AVATAR } from "../../utils/constants";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Info, ChevronRight } from "lucide-react";

const Login = () => {
  const [isSignInForm, setIsSignInForm] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const name = useRef<HTMLInputElement>(null);
  const email = useRef<HTMLInputElement>(null);
  const password = useRef<HTMLInputElement>(null);

  const handleButtonClick = async () => {
    const emailVal = email.current?.value || "";
    const passwordVal = password.current?.value || "";

    const message = checkValidData(emailVal, passwordVal);
    setErrorMessage(message);

    if (message) return;

    setLoading(true);
    if (!isSignInForm) {
      // Sign Up Logic
      createUserWithEmailAndPassword(auth, emailVal, passwordVal)
        .then((userCredential) => {
          const user = userCredential.user;
          updateProfile(user, {
            displayName: name.current?.value || "User",
            photoURL: USER_AVATAR,
          })
            .then(() => {
              const currentUser = auth.currentUser;
              if (currentUser) {
                const { uid, email, displayName, photoURL } = currentUser;
                dispatch(addUser({ uid, email, displayName, photoURL }));
              }
            })
            .catch((error) => setErrorMessage(error.message));
        })
        .catch((error) => setErrorMessage(error.code + " - " + error.message))
        .finally(() => setLoading(false));
    } else {
      // Sign In Logic
      signInWithEmailAndPassword(auth, emailVal, passwordVal)
        .catch((error) => setErrorMessage("Oops! That’s not the right password. Please try again."))
        .finally(() => setLoading(false));
    }
  };

  const toggleSignInForm = () => {
    setIsSignInForm(!isSignInForm);
    setErrorMessage(null);
  };

  return (
    <div className="relative min-h-screen text-white selection:bg-red-600 selection:text-white">
      <Header />
      <div className="absolute inset-0 z-0">
        <img
          className="h-full w-full object-cover brightness-[0.4]"
          src={BG_URL}
          alt="background img"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/60"></div>
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center pt-24 pb-12 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-[450px] p-10 md:p-14 bg-black/75 backdrop-blur-xl rounded-lg shadow-2xl border border-white/5"
        >
          <h1 className="text-3xl md:text-4xl font-bold mb-8 transition-all">
            {isSignInForm ? "Sign In" : "Sign Up"}
          </h1>

          <AnimatePresence mode="wait">
            <motion.form
              key={isSignInForm ? "signin" : "signup"}
              initial={{ opacity: 0, x: isSignInForm ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: isSignInForm ? 20 : -20 }}
              onSubmit={(e: React.FormEvent) => e.preventDefault()}
              className="space-y-4"
            >
              {!isSignInForm && (
                <div className="group relative">
                  <input
                    ref={name}
                    type="text"
                    required
                    className="w-full px-4 py-4 bg-gray-800/60 border border-gray-700/50 rounded focus:outline-none focus:bg-gray-700/80 focus:border-gray-500 transition-all text-white placeholder-gray-500 peer"
                    placeholder=" "
                  />
                  <label className="absolute left-4 top-4 text-gray-500 transition-all pointer-events-none peer-focus:text-[11px] peer-focus:top-1 peer-[:not(:placeholder-shown)]:text-[11px] peer-[:not(:placeholder-shown)]:top-1">
                    Full Name
                  </label>
                </div>
              )}

              <div className="group relative">
                <input
                  ref={email}
                  type="email"
                  required
                  className="w-full px-4 py-4 bg-gray-800/60 border border-gray-700/50 rounded focus:outline-none focus:bg-gray-700/80 focus:border-gray-500 transition-all text-white placeholder-gray-500 peer"
                  placeholder=" "
                />
                <label className="absolute left-4 top-4 text-gray-500 transition-all pointer-events-none peer-focus:text-[11px] peer-focus:top-1 peer-[:not(:placeholder-shown)]:text-[11px] peer-[:not(:placeholder-shown)]:top-1">
                  Email or phone number
                </label>
              </div>

              <div className="group relative">
                <input
                  ref={password}
                  type="password"
                  required
                  className="w-full px-4 py-4 bg-gray-800/60 border border-gray-700/50 rounded focus:outline-none focus:bg-gray-700/80 focus:border-gray-500 transition-all text-white placeholder-gray-500 peer"
                  placeholder=" "
                />
                <label className="absolute left-4 top-4 text-gray-500 transition-all pointer-events-none peer-focus:text-[11px] peer-focus:top-1 peer-[:not(:placeholder-shown)]:text-[11px] peer-[:not(:placeholder-shown)]:top-1">
                  Password
                </label>
              </div>

              {errorMessage && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="bg-[#e87c03] text-white text-[13px] px-5 py-3 rounded mt-2 flex items-start space-x-2"
                >
                  <Info size={16} className="mt-0.5 shrink-0" />
                  <span>{errorMessage}</span>
                </motion.div>
              )}

              <button
                disabled={loading}
                className="w-full py-3.5 mt-4 bg-[#e50914] text-white font-bold rounded hover:bg-[#c11119] transition-all disabled:bg-[#e50914]/50 flex items-center justify-center space-x-2 group active:scale-95"
                onClick={handleButtonClick}
              >
                <span>{loading ? "Authenticating..." : isSignInForm ? "Sign In" : "Sign Up"}</span>
                {!loading && <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />}
              </button>
            </motion.form>
          </AnimatePresence>

          <div className="mt-4 flex items-center justify-between text-[#b3b3b3] text-sm">
            <label className="flex items-center space-x-2 cursor-pointer group">
              <input type="checkbox" className="w-4 h-4 accent-gray-500 bg-gray-800 border-none rounded" defaultChecked />
              <span className="group-hover:text-white transition-colors">Remember me</span>
            </label>
            <span className="hover:underline cursor-pointer">Need help?</span>
          </div>

          <div className="mt-16 space-y-4">
            <p className="text-[#737373]">
              {isSignInForm ? "New to FlixFusion?" : "Already a user?"}{" "}
              <span
                className="text-white hover:underline cursor-pointer font-medium"
                onClick={toggleSignInForm}
              >
                {isSignInForm ? "Sign up now" : "Sign in now"}
              </span>
            </p>
            <div className="flex items-start space-x-2 text-[13px] text-[#8c8c8c] leading-tight">
              <ShieldCheck size={28} className="text-blue-500 shrink-0 mt-0.5" />
              <p>
                This page is protected by Google reCAPTCHA to ensure you're not a bot.{" "}
                <button className="text-[#0071eb] hover:underline">Learn more.</button>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
